import {
  FaFileAlt,
  FaArrowRight,
  FaDollarSign,
  FaArrowUp,
  FaArrowDown,
  FaCheckCircle,
} from "react-icons/fa";
import { HiTrendingUp } from "react-icons/hi";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import { getFlightPostTicketingIconMap } from "./flightReportUtils";

export function FlightPostTicketingSection({
  state,
  feesData,
  onViewChange,
}) {
  const iconMap = getFlightPostTicketingIconMap();

  return state.loading ? (
    <div className="animate-pulse space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="h-48 bg-gray-200/70 rounded-xl"></div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-64 bg-gray-200/70 rounded-xl"></div>
        <div className="h-64 bg-gray-200/70 rounded-xl"></div>
      </div>
    </div>
  ) : state.error ? (
    <div className="text-red-500 text-center py-10">
      {state.message}
      <button
        onClick={state.onRetry}
        className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
      >
        Retry
      </button>
    </div>
  ) : (
    <Card className="p-8 bg-gradient-to-br from-purple-50 via-white to-blue-50 rounded-2xl shadow-lg shadow-purple-100/50 border border-purple-100 hover:shadow-xl transition-all duration-300 overflow-hidden relative">
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-200/20 to-blue-200/20 rounded-full blur-3xl -z-0"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-200/20 to-purple-200/20 rounded-full blur-3xl -z-0"></div>

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <FaFileAlt className="w-7 h-7 text-white" />
            </div>
            <div>
              <span className="text-[#1A1A1A] text-base md:text-lg font-semibold mb-1">
                Flight Post-Ticketing Fees
              </span>
              <p className="text-[#6B7280]">
                Revenue breakdown from all post-booking services and
                modifications
              </p>
            </div>
          </div>
          {onViewChange && (
            <Button
              onClick={() => onViewChange("post-ticketing")}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300"
            >
              View Full Report
              <FaArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          {feesData?.services?.map((service) => {
            const meta = iconMap[service.id];

            return (
              <div
                key={service.id}
                className="bg-white/80 backdrop-blur-sm rounded-xl border border-purple-100 hover:shadow-lg transition-all duration-300 group overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-12 h-12 rounded-lg bg-gradient-to-br ${meta.gradient} flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg ${meta.shadow}`}
                    >
                      {meta.icon}
                    </div>

                    <div className="flex-1">
                      <div className="text-xs text-[#6B7280] uppercase tracking-wider font-medium">
                        {service.label}
                      </div>

                      <div className="text-2xl font-bold text-[#1A1A1A]">
                        ${service.revenue.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#6B7280]">Requests</span>
                      <span className="text-[#1A1A1A] font-semibold text-base md:text-lg">
                        {service.requests.toLocaleString()}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#6B7280]">Avg. Fee</span>
                      <span className="text-[#1A1A1A] font-semibold text-base md:text-lg">
                        ${service.avgFee.toLocaleString()}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#6B7280]">Growth</span>

                      <span
                        className={`font-semibold flex items-center gap-1 ${
                          service.growthPercentage >= 0
                            ? "text-emerald-600"
                            : "text-red-500"
                        }`}
                      >
                        {service.growthPercentage >= 0 ? (
                          <FaArrowUp className="w-3 h-3" />
                        ) : (
                          <FaArrowDown className="w-3 h-3" />
                        )}
                        {service.growthPercentage}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`h-1.5 bg-gradient-to-r ${meta.gradient}`}></div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-purple-100">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <FaDollarSign className="w-5 h-5 text-purple-600" />
              </div>

              <div className="flex-1">
                <span className="text-[#1A1A1A] font-semibold text-base md:text-lg mb-1">
                  Total Post-Ticketing Revenue
                </span>

                <div className="text-3xl font-bold text-purple-600 mb-2">
                  ${feesData?.totalRevenue?.toLocaleString()}
                </div>

                <p className="text-sm text-[#6B7280] leading-relaxed">
                  Combined revenue from all post-ticketing services{" "}
                  {feesData?.totalGrowthPercentage > 0 ? (
                    <span className="font-semibold text-emerald-600">
                      {feesData?.totalGrowthPercentage}% increase
                    </span>
                  ) : (
                    <span className="font-semibold text-red-500">
                      {feesData?.totalGrowthPercentage}% decrease
                    </span>
                  )}{" "}
                  from last month.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-purple-100">
              <div>
                <div className="text-xs text-[#6B7280] mb-1">Total Requests</div>
                <div className="text-xl font-bold text-[#1A1A1A]">
                  {feesData?.totalRequests?.toLocaleString()}
                </div>
              </div>

              <div>
                <div className="text-xs text-[#6B7280] mb-1">
                  Avg. Processing Time
                </div>
                <div className="text-xl font-bold text-[#1A1A1A]">
                  {feesData?.avgProcessingTimeHours?.toFixed(2)} hrs
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-purple-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <HiTrendingUp className="w-5 h-5 text-blue-600" />
              </div>

              <div className="flex-1">
                <span className="text-[#1A1A1A] font-semibold text-base md:text-lg mb-2 block">
                  Performance Insights
                </span>

                <div className="space-y-3">
                  {feesData?.services?.map((service) => (
                    <div key={service.id} className="flex items-start gap-2">
                      <FaCheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />

                      <p className="text-sm text-[#6B7280] leading-relaxed">
                        <span className="font-semibold text-[#1A1A1A]">
                          {service.label}
                        </span>{" "}
                        • {service.requests.toLocaleString()} requests • $
                        {service.revenue.toLocaleString()} revenue •{" "}
                        <span
                          className={
                            service.growthPercentage >= 0
                              ? "text-emerald-600 font-semibold"
                              : "text-red-500 font-semibold"
                          }
                        >
                          {service.growthPercentage}%
                        </span>
                      </p>
                    </div>
                  ))}

                  {!feesData?.services?.length && (
                    <p className="text-sm text-[#6B7280]">
                      No service metrics available
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
